'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DownloadIcon, PackageIcon, PlusIcon, MinusIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface StitchToolProps {
  images: string[];
}

export function StitchTool({ images }: StitchToolProps) {
  const [direction, setDirection] = useState<'vertical' | 'horizontal'>('vertical');
  const [spacing, setSpacing] = useState([0]);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [imageOrder, setImageOrder] = useState<number[]>([]);
  const [downloadOption, setDownloadOption] = useState<'single' | 'zip'>('single');
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loadedImages, setLoadedImages] = useState<HTMLImageElement[]>([]);
  const [previewCanvas, setPreviewCanvas] = useState<HTMLCanvasElement | null>(null);

  // 初始化图片顺序
  useEffect(() => {
    setImageOrder(images.map((_, index) => index));
  }, [images]);

  // 加载所有图片
  useEffect(() => {
    const loadImages = async () => {
      const imgs: HTMLImageElement[] = [];
      for (const src of images) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = src;
        });
        imgs.push(img);
      }
      setLoadedImages(imgs);
    };
    loadImages();
  }, [images]);

  // 绘制预览
  useEffect(() => {
    if (loadedImages.length === 0) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const orderedImages = imageOrder.map((index) => loadedImages[index]).filter(Boolean);
    if (orderedImages.length === 0) return;

    // 预览用的缩放比例
    const previewMaxSize = 600;
    let previewScale = 1;

    if (direction === 'vertical') {
      const totalWidth = Math.max(...orderedImages.map((img) => img.naturalWidth));
      let totalHeight = 0;
      
      orderedImages.forEach((img) => {
        const ratio = totalWidth / img.naturalWidth;
        totalHeight += img.naturalHeight * ratio + (totalHeight > 0 ? spacing[0] : 0);
      });
      
      previewScale = Math.min(previewMaxSize / totalWidth, previewMaxSize / totalHeight, 1);
      canvas.width = totalWidth * previewScale;
      canvas.height = totalHeight * previewScale;
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      let currentY = 0;
      orderedImages.forEach((img, index) => {
        const ratio = totalWidth / img.naturalWidth;
        const width = img.naturalWidth * ratio * previewScale;
        const height = img.naturalHeight * ratio * previewScale;
        const x = (totalWidth * previewScale - width) / 2;
        
        if (index > 0) {
          currentY += spacing[0] * previewScale;
        }
        
        ctx.drawImage(img, x, currentY, width, height);
        currentY += height;
      });
    } else {
      const totalHeight = Math.max(...orderedImages.map((img) => img.naturalHeight));
      let totalWidth = 0;
      
      orderedImages.forEach((img) => {
        const ratio = totalHeight / img.naturalHeight;
        totalWidth += img.naturalWidth * ratio + (totalWidth > 0 ? spacing[0] : 0);
      });
      
      previewScale = Math.min(previewMaxSize / totalWidth, previewMaxSize / totalHeight, 1);
      canvas.width = totalWidth * previewScale;
      canvas.height = totalHeight * previewScale;
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      let currentX = 0;
      orderedImages.forEach((img, index) => {
        const ratio = totalHeight / img.naturalHeight;
        const width = img.naturalWidth * ratio * previewScale;
        const height = img.naturalHeight * ratio * previewScale;
        const y = (totalHeight * previewScale - height) / 2;
        
        if (index > 0) {
          currentX += spacing[0] * previewScale;
        }
        
        ctx.drawImage(img, currentX, y, width, height);
        currentX += width;
      });
    }
    
    setPreviewCanvas(canvas);
  }, [loadedImages, imageOrder, direction, spacing, bgColor]);

  const handleGenerate = useCallback(async () => {
    if (downloadOption === 'zip') {
      // 下载单张预览图打包ZIP（实际应用中可能需要调整）
      await handleDownloadSingle();
    } else {
      await handleDownloadSingle();
    }
  }, [downloadOption]);

  const handleDownloadSingle = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx || loadedImages.length === 0) return;

    const orderedImages = imageOrder.map((index) => loadedImages[index]).filter(Boolean);

    if (direction === 'vertical') {
      const totalWidth = Math.max(...orderedImages.map((img) => img.naturalWidth));
      let totalHeight = 0;
      
      orderedImages.forEach((img) => {
        const ratio = totalWidth / img.naturalWidth;
        totalHeight += img.naturalHeight * ratio + (totalHeight > 0 ? spacing[0] : 0);
      });
      
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      let currentY = 0;
      orderedImages.forEach((img, index) => {
        const ratio = totalWidth / img.naturalWidth;
        const width = img.naturalWidth * ratio;
        const height = img.naturalHeight * ratio;
        const x = (totalWidth - width) / 2;
        
        if (index > 0) {
          currentY += spacing[0];
        }
        
        ctx.drawImage(img, x, currentY, width, height);
        currentY += height;
      });
    } else {
      const totalHeight = Math.max(...orderedImages.map((img) => img.naturalHeight));
      let totalWidth = 0;
      
      orderedImages.forEach((img) => {
        const ratio = totalHeight / img.naturalHeight;
        totalWidth += img.naturalWidth * ratio + (totalWidth > 0 ? spacing[0] : 0);
      });
      
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      let currentX = 0;
      orderedImages.forEach((img, index) => {
        const ratio = totalHeight / img.naturalHeight;
        const width = img.naturalWidth * ratio;
        const height = img.naturalHeight * ratio;
        const y = (totalHeight - height) / 2;
        
        if (index > 0) {
          currentX += spacing[0];
        }
        
        ctx.drawImage(img, currentX, y, width, height);
        currentX += width;
      });
    }

    const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `stitched-${Date.now()}.${outputFormat}`;
          link.click();
          URL.revokeObjectURL(url);
        }
      },
      format,
      0.92
    );
  };

  const handleDownloadOriginalAsZip = async () => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    for (let i = 0; i < images.length; i++) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = images[i];
      });
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      
      const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, format, 0.92);
      });
      
      if (blob) {
        zip.file(`original-${i + 1}.${outputFormat}`, blob);
      }
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `original-images-${Date.now()}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    setImageOrder((prev) => {
      const newOrder = [...prev];
      const [removed] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, removed);
      return newOrder;
    });
  };

  const getOutputSize = () => {
    if (loadedImages.length === 0) return { width: 0, height: 0 };
    
    const orderedImages = imageOrder.map((index) => loadedImages[index]).filter(Boolean);
    
    if (direction === 'vertical') {
      const totalWidth = Math.max(...orderedImages.map((img) => img.naturalWidth));
      let totalHeight = 0;
      
      orderedImages.forEach((img, index) => {
        const ratio = totalWidth / img.naturalWidth;
        totalHeight += img.naturalHeight * ratio + (totalHeight > 0 ? spacing[0] : 0);
      });
      
      return { width: totalWidth, height: totalHeight };
    } else {
      const totalHeight = Math.max(...orderedImages.map((img) => img.naturalHeight));
      let totalWidth = 0;
      
      orderedImages.forEach((img) => {
        const ratio = totalHeight / img.naturalHeight;
        totalWidth += img.naturalWidth * ratio + (totalWidth > 0 ? spacing[0] : 0);
      });
      
      return { width: totalWidth, height: totalHeight };
    }
  };

  const outputSize = getOutputSize();

  return (
    <div className="space-y-6">
      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border bg-muted/50 p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">拼接方向</Label>
            <Select value={direction} onValueChange={(val) => setDirection(val as 'vertical' | 'horizontal')}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">纵向拼接</SelectItem>
                <SelectItem value="horizontal">横向拼接</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">间距</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSpacing([Math.max(0, spacing[0] - 5)])}
              >
                <MinusIcon className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-sm">{spacing[0]}px</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSpacing([spacing[0] + 5])}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">背景</Label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="h-8 w-12 cursor-pointer rounded border"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">格式</Label>
            <Select value={outputFormat} onValueChange={(val) => setOutputFormat(val as 'png' | 'jpeg' | 'webp')}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="jpeg">JPEG</SelectItem>
                <SelectItem value="webp">WebP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* 预览和设置 */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* 图片排序 */}
        <div className="space-y-4">
          <h3 className="font-medium">图片排序</h3>
          <p className="text-sm text-muted-foreground">
            点击上下箭头调整图片顺序
          </p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {imageOrder.map((imageIndex, displayIndex) => (
              <div
                key={`${imageIndex}-${displayIndex}`}
                className="flex items-center gap-2 rounded-lg border p-2"
              >
                <span className="w-6 text-sm text-muted-foreground">
                  {displayIndex + 1}.
                </span>
                <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded border">
                  <img
                    src={images[imageIndex]}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                <span className="flex-1 truncate text-sm">
                  图片 {imageIndex + 1}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={displayIndex === 0}
                    onClick={() => moveImage(displayIndex, displayIndex - 1)}
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={displayIndex === imageOrder.length - 1}
                    onClick={() => moveImage(displayIndex, displayIndex + 1)}
                  >
                    <ArrowDownIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 预览 */}
        <div className="space-y-4">
          <h3 className="font-medium">预览</h3>
          <div className="flex items-center justify-center rounded-lg border bg-muted p-4 overflow-auto max-h-[500px]">
            {previewCanvas ? (
              <img
                src={previewCanvas.toDataURL()}
                alt="预览"
                className="max-w-full"
              />
            ) : (
              <div className="text-muted-foreground">加载中...</div>
            )}
          </div>
          <div className="text-center text-sm text-muted-foreground">
            输出尺寸: {outputSize.width} x {outputSize.height} px
          </div>
        </div>

        {/* 操作 */}
        <div className="space-y-4">
          <h3 className="font-medium">设置说明</h3>
          <div className="rounded-lg border bg-card p-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• 选择纵向或横向拼接</li>
              <li>• 调整图片之间的间距</li>
              <li>• 使用上下箭头调整图片顺序</li>
              <li>• 图片会自动缩放以保持一致宽度/高度</li>
              <li>• 点击下载生成拼接图</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Button className="w-full" size="lg" onClick={handleGenerate}>
              <DownloadIcon className="mr-2 h-4 w-4" />
              下载拼接图
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={handleDownloadOriginalAsZip}
            >
              <PackageIcon className="mr-2 h-4 w-4" />
              下载原图 ZIP ({imageOrder.length} 张)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
